<script setup lang="ts">
/**
 * A company's or a team's KPIs as cards: each one as it was set and as it is doing, with its source named and the
 * form to enter a reading. The people and tasks a source is named by are read once here, for every card, and so are
 * the cards' readings (issue #556, Sentry OS-PORTAL-3): one batched read for all the KPIs, whatever their number,
 * instead of one per card. Each card is handed its own KPI's readings and does not read them itself.
 *
 * The read is keyed on the set of KPI ids: adding or removing a KPI reads again, a poll that hands back the same
 * KPIs does not. Recording a reading on a card reads the batch once more. Only the newest read's answer is kept, so
 * an answer that arrives after the KPIs changed is dropped.
 */
import { computed, ref, watch } from 'vue'
import type { KpiReadingRecord } from '@/api'
import KpiTargetCard from '@/components/KpiTargetCard.vue'
import { useKpiSources } from '@/composables/useKpiSources'
import { kpiReadingsApi } from '@/kpi-readings-api'
import type { Kpi } from '@/lib/kpi'

const props = defineProps<{ kpis: Kpi[] }>()
/** A reading was recorded on one of the cards, so anything else showing these KPIs' lights is out of date. */
const emit = defineEmits<{ recorded: [] }>()

const sources = useKpiSources()

/**
 * Every card's readings by KPI id, from the one batched read. `null` until a read answers, so when the first read
 * fails a card handed `null` shows what a card whose own read failed shows, which is nothing about its readings. A
 * later read that fails (the refresh after a record, or a re-read for a changed id set) keeps the last answer
 * rather than blanking every card; a KPI that answer does not name still shows what a failed read shows.
 */
const readings = ref<Record<string, KpiReadingRecord[]> | null>(null)

/** The KPIs the read names, in the order the list shows them. */
const kpiIds = computed(() => props.kpis.map(kpi => kpi.id))
/** What the read is keyed on: the set of ids, so a poll that reorders nothing and adds nothing reads nothing. */
const idsKey = computed(() => [...kpiIds.value].sort().join(','))

/** Counts reads, so an answer to any read but the newest is dropped. */
let latestRead = 0

async function loadReadings(): Promise<void> {
    latestRead += 1
    const thisRead = latestRead
    const ids = kpiIds.value
    if (ids.length === 0) {
        readings.value = {}
        return
    }
    const loaded = await kpiReadingsApi.batch(ids).catch(() => null)
    // A failed re-read keeps the last answer: one blip after a record must not blank every card.
    if (thisRead === latestRead && loaded !== null) {
        readings.value = loaded
    }
}

watch(idsKey, loadReadings, { immediate: true })

/** One KPI's readings as its card takes them; a KPI missing from the answer shows what a failed read shows. */
function readingsOf(kpiId: string): KpiReadingRecord[] | null {
    return readings.value?.[kpiId] ?? null
}

/** A card recorded a reading: read the batch again, once, then tell the page. */
async function onRecorded(): Promise<void> {
    await loadReadings()
    emit('recorded')
}
</script>

<template>
  <div class="grid gap-2 sm:grid-cols-2">
    <KpiTargetCard
      v-for="kpi in kpis"
      :key="kpi.id"
      :kpi="kpi"
      :source-name="sources.name(kpi.sourceKind, kpi.sourceRef)"
      readable
      :readings="readingsOf(kpi.id)"
      @recorded="onRecorded"
    />
  </div>
</template>
